import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Custom form hook similar to Formik with validation like Yup
 * @param {Object} initialValues - Initial form values
 * @param {Object} validationSchema - Validation schema object
 * @param {Function} onSubmit - Submit handler function
 * @param {Object} options - Additional options
 * @returns {Object} Form state and handlers
 */
const useForm = ({
  initialValues = {},
  validationSchema = {},
  onSubmit = () => {},
  options = {},
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const touched = useRef({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [dirty, setDirty] = useState(false);

  const initialValuesRef = useRef(initialValues);
  const validationSchemaRef = useRef(validationSchema);
  const onSubmitRef = useRef(onSubmit);
  const optionsRef = useRef(options);

  // Update refs when props change
  useEffect(() => {
    initialValuesRef.current = initialValues;
    validationSchemaRef.current = validationSchema;
    onSubmitRef.current = onSubmit;
    optionsRef.current = options;
  }, [initialValues, validationSchema, onSubmit, options]);

  /**
   * Validate a single field
   * @param {string} fieldName - Field name to validate
   * @param {any} value - Field value
   * @returns {string|null} Error message or null if valid
   */
  const validateField = useCallback(
    (fieldName, value) => {
      const schema = validationSchemaRef.current[fieldName];
      if (!schema) return null;

      // Required validation
      if (
        schema.required &&
        (!value || (typeof value === 'string' && value.trim() === ''))
      ) {
        return schema.required === true
          ? t('fieldIsRequired', { fieldName })
          : schema.required;
      }

      // Skip other validations if value is empty and not required
      if (!value && !schema.required) return null;

      // Min/Max length validation
      if (schema.minLength && value.length < schema.minLength) {
        return (
          schema.minLengthMessage ||
          t('fieldMinLength', { fieldName, minLength: schema.minLength })
        );
      }

      if (schema.maxLength && value.length > schema.maxLength) {
        return (
          schema.maxLengthMessage ||
          t('fieldMaxLength', { fieldName, maxLength: schema.maxLength })
        );
      }

      // Min/Max value validation
      if (schema.min !== undefined && Number(value) < schema.min) {
        return (
          schema.minMessage ||
          t('fieldMinValue', { fieldName, min: schema.min })
        );
      }

      if (schema.max !== undefined && Number(value) > schema.max) {
        return (
          schema.maxMessage ||
          t('fieldMaxValue', { fieldName, max: schema.max })
        );
      }

      // Pattern validation
      if (schema.pattern) {
        const regex = new RegExp(schema.pattern);
        if (!regex.test(value)) {
          return (
            schema.patternMessage || t('fieldInvalidFormat', { fieldName })
          );
        }
      }

      // Custom validation
      if (schema.validate) {
        const customError = schema.validate(value, values);
        if (customError) return customError;
      }

      return null;
    },
    [values],
  );

  /**
   * Set field touched state
   * @param {string} fieldName - Field name
   * @param {boolean} touched - Touched state
   */
  const setFieldTouched = useCallback(
    (fieldName, isTouched = true) => {
      touched.current[fieldName] = isTouched;

      // Validate field when touched
      if (isTouched) {
        const error = validateField(fieldName, values[fieldName]);
        setErrors((prev) => ({
          ...prev,
          [fieldName]: error,
        }));
      }
    },
    [values, validateField],
  );
  /**
   * Validate all fields
   * @returns {Object} Object with field names as keys and error messages as values
   */
  const validateForm = useCallback(() => {
    const newErrors = {};
    let hasErrors = false;

    Object.keys(validationSchemaRef.current).forEach((fieldName) => {
      const error = validateField(fieldName, values[fieldName]);
      setFieldTouched(fieldName, true);
      if (error) {
        newErrors[fieldName] = error;
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    setIsValid(!hasErrors);
    return { errors: newErrors, isValid: !hasErrors };
  }, [values, validateField]);

  /**
   * Set field value
   * @param {string} fieldName - Field name
   * @param {any} value - Field value
   */
  const setFieldValue = useCallback(
    (fieldName, value) => {
      setValues((prev) => {
        const newValues = { ...prev, [fieldName]: value };

        // Mark as dirty if value changed from initial
        if (newValues[fieldName] !== initialValuesRef.current[fieldName]) {
          setDirty(true);
        }

        return newValues;
      });

      // Validate field if touched
      if (touched.current[fieldName]) {
        const error = validateField(fieldName, value);
        setErrors((prev) => ({
          ...prev,
          [fieldName]: error,
        }));
      }
    },
    [touched.current, validateField],
  );

  /**
   * Set field error
   * @param {string} fieldName - Field name
   * @param {string} error - Error message
   */
  const setFieldError = useCallback((fieldName, error) => {
    setErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  }, []);

  /**
   * Handle field change
   * @param {string} fieldName - Field name
   * @param {any} value - Field value
   */
  const handleChange = useCallback(
    (value) => (fieldName) => {
      setFieldValue(fieldName, value);
    },
    [setFieldValue],
  );

  /**
   * Handle field blur
   * @param {string} fieldName - Field name
   */
  const handleBlur = useCallback(
    (fieldName) => {
      setFieldTouched(fieldName, true);
    },
    [setFieldTouched],
  );

  /**
   * Reset form to initial values
   */
  const resetForm = useCallback(() => {
    setValues(initialValuesRef.current);
    setErrors({});
    touched.current = {};
    setIsSubmitting(false);
    setIsValidating(false);
    setIsValid(false);
    setDirty(false);
  }, []);

  /**
   * Submit form
   */
  const submitForm = useCallback(async () => {
    setIsValidating(true);

    try {
      const validation = validateForm();

      setIsSubmitting(true);
      if (!validation.isValid) {
        setIsValidating(false);
        return;
      }

      await onSubmitRef.current(values, { resetForm, setFieldError });

      if (optionsRef.current.resetOnSubmit !== false) {
        resetForm();
      }
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
        setIsValidating(false);
      }, 100);
    }
  }, [values, validateForm, resetForm]);

  /**
   * Handle form submit event
   * @param {Event} event - Form submit event
   */
  const handleSubmit = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
      }
      submitForm();
    },
    [submitForm],
  );

  // Validate form when values change (if validateOnChange is enabled)
  useEffect(() => {
    if (
      optionsRef.current.validateOnChange &&
      Object.keys(touched.current).length > 0
    ) {
      validateForm();
    }
  }, [values, validateForm]);

  return {
    // Values
    values,
    errors,
    touched: touched.current,
    isSubmitting,
    isValidating,
    isValid,
    dirty,

    // Handlers
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    setValues,
    resetForm,
    submitForm,
    validateForm,
    validateField,
    setErrors,
  };
};

export default useForm;
